import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { RefinanceState } from '@/store/slices/refinanceSlice';
import { calculateHELOANClosingDisclosure } from '@/utils/calculator/HELOAN';
import { calculateHELOCClosingDisclosure } from '@/utils/calculator/HELOC';
import { calculateSecondMortgageClosingDisclosure } from '@/utils/calculator/SecondMortgage';
import {
  calculateBridgeRefinanceClosingDisclosure,
  calculateCommercialClosingDisclosure,
  calculateConventionalClosingDisclosure,
  calculateDefaultClosingDisclosure,
  calculateDSCRClosingDisclosure,
  calculateFHAClosingDisclosure,
  calculateFixAndFlipClosingDisclosure,
  calculateUSDAClosingDisclosure,
  calculateVAClosingDisclosure,
} from '@/utils/refinanceCalculator';
import type { Product } from '@/types/ProductType';

export function newLoanAmountFromRefinance(
  ref: NonNullable<Product['refinance']>
): number {
  const refiType = ref.selection?.refiType;
  const balance = ref.currentLoan?.balance ?? 0;
  if (['heloc', 'heloan', 'second_mortgage'].includes(String(refiType || ''))) {
    return ref.newLoan?.requestedLoanAmount || balance;
  }
  if (refiType === 'cash_out') {
    return balance + (ref.newLoan?.cashOutAmount || 0);
  }
  return balance;
}

export function getRefinanceImpliedEquityDown(product: Product): number {
  const ref = product.refinance;
  if (!ref) return 0;
  const value = ref.property?.value ?? product.price ?? 0;
  return Math.max(0, value - newLoanAmountFromRefinance(ref));
}

/**
 * Closing-disclosure-style estimate for a refinance product, aligned with
 * RefinanceSummaryPage / refinance calculators (not purchase calculateClosingDisclosure).
 */
export function estimateRefinanceProduct(
  product: Product,
  state: RootState,
  overrides?: { loanProgram?: string; downPayment?: number }
) {
  const ref = product.refinance;
  if (!ref || product.transactionMode !== 'refinance') return null;

  const value = ref.property?.value ?? product.price ?? 0;
  const newLoanAmount = newLoanAmountFromRefinance(ref);
  const impliedEquityDown = Math.max(0, value - newLoanAmount);
  const downPayment =
    overrides?.downPayment !== undefined
      ? overrides.downPayment
      : impliedEquityDown;

  const programRaw =
    overrides?.loanProgram ||
    product.savedLoanType ||
    ref.selection?.program ||
    USER_LOAN_PROGRAM.CONVENTIONAL;
  const program = String(programRaw).toLowerCase() as USER_LOAN_PROGRAM;

  const equityLineOrLoan =
    ref.newLoan?.requestedLoanAmount || ref.currentLoan?.balance || 0;

  const zipStr = ref.property?.zip || product.address?.zip || '';
  const estimationInput = {
    offerPrice: value,
    listPrice: value,
    downPayment,
    zipCode: Number(String(zipStr).replace(/\D/g, '').slice(0, 5)) || 0,
    hoaFees: ref.currentLoan?.escrowHoa ?? 0,
    commission: 0,
    marketInterestRate: ref.currentLoan?.rate ?? 6.75,
    creditScore: ref.newLoan?.creditScore ?? 700,
    loanTerm: ref.newLoan?.termYears ?? 30,
    currentLoanBalance: ref.currentLoan?.balance ?? 0,
    requestedLoanAmount: equityLineOrLoan,
    requestedCreditLine: equityLineOrLoan,
  };

  switch (program) {
    case USER_LOAN_PROGRAM.CONVENTIONAL:
      return calculateConventionalClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.FHA:
      return calculateFHAClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.VA:
      return calculateVAClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.USDA:
      return calculateUSDAClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.DSCR:
      return calculateDSCRClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.FIX_AND_FLIP:
      return calculateFixAndFlipClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.COMMERCIAL:
      return calculateCommercialClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.BRIDGE: {
      const totalLoan =
        estimationInput.offerPrice - estimationInput.downPayment;
      const existingLoanBalance = ref.currentLoan?.balance ?? 0;
      const cashOutAmount = Math.max(0, totalLoan - existingLoanBalance);
      const loanTermMonths = Math.min(
        24,
        Math.max(6, estimationInput.loanTerm * 12)
      );
      return calculateBridgeRefinanceClosingDisclosure(
        {
          currentPropertyValue: estimationInput.offerPrice,
          existingLoanBalance,
          cashOutAmount,
          zipCode: estimationInput.zipCode,
          hoaFees: estimationInput.hoaFees,
          marketInterestRate: estimationInput.marketInterestRate,
          creditScore: estimationInput.creditScore,
          loanTerm: loanTermMonths,
        },
        state
      );
    }
    case USER_LOAN_PROGRAM.HELOC:
      return calculateHELOCClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.HELOAN:
      return calculateHELOANClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.SECOND_MORTGAGE:
      return calculateSecondMortgageClosingDisclosure(estimationInput, state);
    default:
      return calculateDefaultClosingDisclosure(estimationInput, state);
  }
}

/**
 * Merge live Redux refinance intake with the API product so hooks like useEstimation
 * see the same numbers as the refinance summary header cards.
 */
export function mergeRefinanceSliceIntoProduct(
  product: Product,
  slice: RefinanceState
): Product {
  const zipDigits = String(slice.property.zipCode || '').replace(/\D/g, '');
  const zipNum = zipDigits ? parseInt(zipDigits.slice(0, 5), 10) : undefined;

  return {
    ...product,
    transactionMode: 'refinance',
    price: slice.property.value || product.price,
    savedLoanType: slice.selection.program,
    loanInfo: {
      ...product.loanInfo,
      offerPrice:
        slice.property.value || product.loanInfo?.offerPrice || product.price,
      ...(zipNum != null && !Number.isNaN(zipNum) ? { zipCode: zipNum } : {}),
    },
    refinance: {
      ...product.refinance,
      currentLoan: {
        rate: slice.currentLoan.rate,
        balance: slice.currentLoan.balance,
        termYears: slice.currentLoan.termYears,
        monthlyPayment: slice.currentLoan.monthlyPayment,
        escrowTaxes: slice.currentLoan.escrowTaxes,
        escrowInsurance: slice.currentLoan.escrowInsurance,
        escrowHoa: slice.currentLoan.escrowHoa,
      },
      newLoan: {
        termYears: slice.newLoan.termYears,
        creditScore: slice.newLoan.creditScore,
        cashOutAmount: slice.newLoan.cashOutAmount,
        requestedLoanAmount: slice.newLoan.requestedLoanAmount,
      },
      property: {
        street: slice.property.address,
        city: slice.property.city,
        state: slice.property.state,
        zip: slice.property.zipCode,
        fullAddress: product.address?.fullAddress,
        value: slice.property.value,
        propertyType: slice.property.propertyType,
      },
      selection: {
        program: slice.selection.program,
        refiType: slice.selection.refiType,
      },
    },
  };
}
